(function () {
  'use strict';

  const API_URL = 'https://api.armastus.app/api/v1/armastus/public/category-menu';
  const CACHE_TTL = 60 * 60 * 1000;

  // ── Locale island ──────────────────────────────────────────────────────────
  let _locale = null;
  function getLocale() {
    if (_locale) return _locale;
    try {
      const el = document.getElementById('armastus-cmenu-locale');
      _locale = el ? JSON.parse(el.textContent) : {};
    } catch { _locale = {}; }
    return _locale;
  }
  function getLang() { return getLocale().lang || 'en'; }
  function isRTL() {
    const l = getLang();
    return l === 'ar' || l.startsWith('ar-') || document.documentElement.dir === 'rtl';
  }

  // ── String helpers ─────────────────────────────────────────────────────────
  function t(obj) {
    if (!obj) return '';
    const l = getLang();
    return (l.startsWith('ar') ? obj.ar : obj.en) || obj.en || obj.ar || '';
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatPrice(amount, currency) {
    if (amount == null || amount === '') return '';
    try {
      return new Intl.NumberFormat(getLang(), {
        style: 'currency',
        currency: currency || getLocale().currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount));
    } catch {
      return `${currency || ''} ${parseFloat(amount).toFixed(2)}`.trim();
    }
  }

  function shopifyImg(url, width, quality) {
    if (!url) return url;
    const sep = url.includes('?') ? '&' : '?';
    const q = quality ? `&quality=${quality}` : '';
    return `${url}${sep}width=${width}&format=webp${q}`;
  }

  // ── Product cache ──────────────────────────────────────────────────────────
  const _productCache = {};
  async function fetchProducts(handle, limit) {
    const key = `${handle}:${limit}`;
    if (_productCache[key]) return _productCache[key];
    try {
      const r = await fetch(
        `/collections/${encodeURIComponent(handle)}/products.json?limit=${limit}&sort_by=best-selling`,
        { headers: { Accept: 'application/json' } }
      );
      if (!r.ok) return [];
      const { products } = await r.json();
      const out = (products || []).filter(p => p.available !== false).slice(0, limit);
      _productCache[key] = out;
      return out;
    } catch { return []; }
  }

  // ── Swatches ───────────────────────────────────────────────────────────────
  function getSwatches(product, maxVisible) {
    const colorOpt = (product.options || []).find(o => /colou?r|لون/i.test(o.name));
    if (!colorOpt) return { swatches: [], total: 0 };
    const key = `option${colorOpt.position}`;
    const seen = new Set();
    const all = [];
    for (const v of (product.variants || [])) {
      const val = v[key];
      if (!val || seen.has(val)) continue;
      seen.add(val);
      all.push({ label: val, imgSrc: v.featured_image ? v.featured_image.src : null });
    }
    return {
      swatches: maxVisible ? all.slice(0, maxVisible) : all,
      total: all.length,
    };
  }

  // ── Renderers ──────────────────────────────────────────────────────────────

  function badgeHtml(badge) {
    if (!badge) return '';
    const text = t(badge.text);
    if (!text) return '';
    const pos = badge.position || 'top-right';
    return `<span class="armcm-badge armcm-badge--${esc(pos)}" style="background:${esc(badge.bgColor || '#ef4444')};color:${esc(badge.textColor || '#fff')}">${esc(text)}</span>`;
  }

  function circleItemHtml(col) {
    const name = t(col.name);
    const imgSrc = col.imageUrl ? shopifyImg(col.imageUrl, 200, 80) : '';
    return `<a href="/collections/${esc(col.collectionHandle)}" class="armcm-circle-item">
      <span class="armcm-circle-img">
        ${imgSrc
          ? `<img src="${esc(imgSrc)}" alt="${esc(name)}" loading="lazy">`
          : `<span class="armcm-circle-placeholder">${esc(name ? name[0].toUpperCase() : '?')}</span>`}
        ${badgeHtml(col.badge)}
      </span>
      <span class="armcm-circle-label">${esc(name)}</span>
    </a>`;
  }

  function renderCollectionCircles(data) {
    const cols = data.columns || 3;
    const enabled = (data.collections || [])
      .filter(c => c.enabled)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    return `<div class="armcm-circles-grid" style="grid-template-columns:repeat(${cols},1fr)">
      ${enabled.map(circleItemHtml).join('')}
    </div>`;
  }

  function renderCustomText(data) {
    const title = t(data.title);
    const subtitle = t(data.subtitle);
    if (!title && !subtitle) return '';
    const align = data.alignment || 'left';
    const WEIGHTS = { normal: '400', medium: '500', semibold: '600', bold: '700' };
    const bgStyle = data.backgroundColor
      ? `background:${esc(data.backgroundColor)};border-radius:6px;margin:0 12px;`
      : '';
    return `<div class="armcm-custom-text" style="text-align:${esc(align)};padding:8px 12px;${bgStyle}">
      ${title ? `<div style="font-size:${+(data.titleFontSize)||16}px;font-weight:${WEIGHTS[data.titleFontWeight] || '600'};color:${esc(data.titleColor || '#1f2937')}">${esc(title)}</div>` : ''}
      ${subtitle ? `<div style="font-size:${+(data.subtitleFontSize)||12}px;font-weight:${WEIGHTS[data.subtitleFontWeight] || '400'};color:${esc(data.subtitleColor || '#6b7280')};margin-top:${title ? '4px' : '0'}">${esc(subtitle)}</div>` : ''}
    </div>`;
  }

  function renderDivider(data) {
    const w = data.widthPercent ? `${data.widthPercent}%` : '100%';
    const mt = data.marginTop != null ? data.marginTop : 4;
    const mb = data.marginBottom != null ? data.marginBottom : 4;
    const align = data.alignment || 'center';
    const flex = align === 'right' ? 'flex-end' : align === 'left' ? 'flex-start' : 'center';
    return `<div style="display:flex;justify-content:${flex};padding:0 12px;margin-top:${mt}px;margin-bottom:${mb}px">
      <hr style="width:${w};border:none;border-top:${+(data.thickness)||1}px ${data.style || 'solid'} ${esc(data.color || '#e5e7eb')};margin:0">
    </div>`;
  }

  function renderProductGridSkeleton(data, idx) {
    const cols = data.columns || 2;
    const count = Math.min(data.limit || 4, cols * 2);
    const skeletons = Array.from({ length: count }, () => '<span class="armcm-product-skeleton"></span>').join('');
    const rtl = isRTL();
    const viewAllLabel = data.viewAllText ? esc(t(data.viewAllText)) : (rtl ? '&#x2190; &#x639;&#x631;&#x636; &#x627;&#x644;&#x643;&#x644;' : 'View All &#x2192;');
    const viewAllHtml = data.showViewAll
      ? `<a href="/collections/${esc(data.collectionHandle)}" class="armcm-view-all" style="font-size:${+(data.viewAllFontSize)||12}px;padding:${+(data.viewAllPaddingVertical)||10}px 12px">${viewAllLabel}</a>`
      : '';
    return `<div class="armcm-product-grid" data-armcm-pg-idx="${idx}">
      <div class="armcm-product-grid-inner" style="grid-template-columns:repeat(${cols},1fr)">${skeletons}</div>
      ${viewAllHtml}
    </div>`;
  }

  function productCardHtml(product, imgWidth, data) {
    const imgSrc = (product.images || [])[0]?.src
      ? shopifyImg(product.images[0].src, imgWidth, 80)
      : '';
    const currency = getLocale().currency || 'USD';
    const variant0 = (product.variants || [])[0];
    const price = variant0?.price;
    const compare = variant0?.compare_at_price;
    const showSwatches = data.showSwatches;
    const swatchInfoFull = showSwatches ? getSwatches(product, 0) : { swatches: [], total: 0 };
    const allSwatches = swatchInfoFull.swatches;
    const visibleCount = allSwatches.length > 3 ? 2 : 3;
    const moreCount = Math.max(0, allSwatches.length - visibleCount);

    const swatchesHtml = allSwatches.length > 1
        ? `<div class="armcm-swatches">${allSwatches.map((s, i) => {
            const inner = s.imgSrc
              ? `<img class="armcm-swatch" src="${esc(shopifyImg(s.imgSrc, 60, 80))}" alt="${esc(s.label)}" loading="lazy">`
              : `<span class="armcm-swatch armcm-swatch--text" title="${esc(s.label)}">${esc(s.label.substring(0, 3))}</span>`;
            const imgAttr = s.imgSrc ? ` data-armcm-img="${esc(shopifyImg(s.imgSrc, imgWidth, 80))}"` : '';
            const hiddenCls = i >= visibleCount ? ' armcm-swatch-btn--hidden' : '';
            return `<span class="armcm-swatch-btn${i === 0 ? ' armcm-swatch-btn--active' : ''}${hiddenCls}" role="button" tabindex="0"${imgAttr} aria-label="${esc(s.label)}">${inner}</span>`;
          }).join('')}${moreCount > 0 ? `<button type="button" class="armcm-swatch-more" data-armcm-more aria-label="+${moreCount} more">+${moreCount}</button>` : ''}</div>`
        : '';

    const priceHtml = data.showPrice !== false && price
      ? `<span class="armcm-product-price">${esc(formatPrice(price, currency))}</span>`
      : '';
    const compareHtml = data.showCompareAtPrice !== false && compare && parseFloat(compare) > parseFloat(price)
      ? `<span class="armcm-product-compare">${esc(formatPrice(compare, currency))}</span>`
      : '';

    return `<a class="armcm-product-card" href="/products/${esc(product.handle)}">
      <span class="armcm-product-img">${imgSrc ? `<img src="${esc(imgSrc)}" alt="${esc(product.title)}" loading="lazy">` : ''}</span>
      ${swatchesHtml}
      ${data.showTitle || priceHtml || compareHtml ? `<span class="armcm-product-info">
        ${data.showTitle ? `<span class="armcm-product-title">${esc(product.title)}</span>` : ''}
        ${priceHtml || compareHtml ? `<span class="armcm-product-prices">${priceHtml}${compareHtml}</span>` : ''}
      </span>` : ''}
    </a>`;
  }

  async function fillProductGrid(container, data, idx) {
    const wrapEl = container.querySelector(`.armcm-product-grid[data-armcm-pg-idx="${idx}"]`);
    if (!wrapEl || !document.contains(wrapEl)) return;
    const innerEl = wrapEl.querySelector('.armcm-product-grid-inner');
    if (!innerEl) return;

    const cols = data.columns || 2;
    const panelWidth = container.offsetWidth || Math.round(window.innerWidth * 0.78);
    const imgWidth = Math.round((panelWidth - 24 - 8 * (cols - 1)) / cols) * 2;

    const products = await fetchProducts(data.collectionHandle, data.limit || 8);
    if (!document.contains(innerEl)) return;
    if (!products.length) { wrapEl.remove(); return; }
    innerEl.innerHTML = products.map(p => productCardHtml(p, imgWidth, data)).join('');
  }

  function renderSections(sections, idx0) {
    const enabled = sections.filter(s => s.enabled).sort((a, b) => a.sortOrder - b.sortOrder);
    let pgCounter = idx0;
    return { html: enabled.map(sec => {
      switch (sec.type) {
        case 'collection_circles': return renderCollectionCircles(sec.data);
        case 'product_grid':       return renderProductGridSkeleton(sec.data, pgCounter++);
        case 'custom_text':        return renderCustomText(sec.data);
        case 'divider':            return renderDivider(sec.data);
        default:                   return '';
      }
    }).join(''), pgCount: pgCounter - idx0, sections: enabled };
  }

  // ── Custom Element ─────────────────────────────────────────────────────────
  class ArmastusCategoryMenu extends HTMLElement {
    constructor() {
      super();
      this._config = null;
      this._fetchTs = 0;
      this._selectedMainId = null;
      this._selectedSubId = null;
      this._prevFocus = null;
    }

    connectedCallback() {
      const bind = () => {
        // Global trigger: any a[href="#category-menu"] or [data-category-menu-open]
        document.body.addEventListener('click', e => {
          if (e.target.closest('a[href$="#category-menu"],[data-category-menu-open]')) {
            e.preventDefault();
            this.toggle();
            return;
          }
          if (e.target.closest('.armcm-close') || e.target === this.querySelector('.armcm-backdrop')) {
            this.close();
          }
        });
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bind, { once: true });
      } else {
        bind();
      }

      // Internal navigation (tabs, subcategories)
      this.addEventListener('click', e => {
          const tab = e.target.closest('[data-armcm-tab]');
          if (tab) { e.preventDefault(); this._selectMain(tab.dataset.armcmTab); return; }
          const sub = e.target.closest('[data-armcm-sub]');
          if (sub) { e.preventDefault(); this._selectSub(sub.dataset.armcmSub); return; }
          const more = e.target.closest('[data-armcm-more]');
          if (more) {
            e.preventDefault();
            e.stopPropagation();
            const wrap = more.closest('.armcm-swatches');
            if (wrap) {
              wrap.querySelectorAll('.armcm-swatch-btn--hidden').forEach(b => b.classList.remove('armcm-swatch-btn--hidden'));
              more.remove();
            }
            return;
          }
          const swatch = e.target.closest('.armcm-swatch-btn');
          if (swatch) {
            e.preventDefault();
            e.stopPropagation();
            const card = swatch.closest('.armcm-product-card');
            if (!card) return;
            const img = card.querySelector('.armcm-product-img img');
            const newSrc = swatch.dataset.armcmImg;
            if (img && newSrc) img.src = newSrc;
            card.querySelectorAll('.armcm-swatch-btn').forEach(b => b.classList.remove('armcm-swatch-btn--active'));
            swatch.classList.add('armcm-swatch-btn--active');
          }
        });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.classList.contains('armcm-open')) this.close();
      });

      window.addEventListener('popstate', () => {
        if (this.classList.contains('armcm-open') && !location.hash.startsWith('#category-menu')) {
          this._closeVisual();
        }
      });
    }

    async open() {
        this._prevFocus = document.activeElement;
        this.removeAttribute('hidden');
        this.removeAttribute('aria-hidden');
        document.documentElement.classList.add('armcm-html-active');

        const headerEl =
          document.querySelector('#shopify-section-group-header-group') ||
          document.querySelector('.shopify-section-group-header-group') ||
          document.querySelector('header[role="banner"]') ||
          document.querySelector('.header');
        if (headerEl) {
          const rect = headerEl.getBoundingClientRect();
          if (rect.height > 0) this.style.setProperty('--armcm-header-offset', `${rect.bottom}px`);
        }

        if (isRTL()) {
          this.classList.add('armcm-rtl');
          this.setAttribute('dir', 'rtl');
        } else {
          this.classList.remove('armcm-rtl');
          this.setAttribute('dir', 'ltr');
        }

        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('armcm-open')));

      if (!location.hash.startsWith('#category-menu')) {
        history.pushState({ armcmOpen: true }, '', '#category-menu');
      }

      if (!this._config || Date.now() - this._fetchTs > CACHE_TTL) {
        await this._fetchAndRender();
      }

      const firstFocusable = this.querySelector('button, [href]');
      if (firstFocusable) firstFocusable.focus();
    }

    close(updateHistory = true) {
      this._closeVisual();
      if (updateHistory && location.hash === '#category-menu') history.back();
    }

    _closeVisual() {
      this.classList.remove('armcm-open');
      document.documentElement.classList.remove('armcm-html-active');
      this.setAttribute('aria-hidden', 'true');
      const onEnd = () => {
        if (!this.classList.contains('armcm-open')) this.setAttribute('hidden', '');
        this.removeEventListener('transitionend', onEnd);
      };
      this.addEventListener('transitionend', onEnd);
      if (this._prevFocus) { this._prevFocus.focus(); this._prevFocus = null; }
    }

    toggle() { this.classList.contains('armcm-open') ? this.close() : this.open(); }

    async refresh() {
      this._config = null;
      this._fetchTs = 0;
      if (this.classList.contains('armcm-open')) await this._fetchAndRender();
    }

    get config() { return this._config; }

    async _fetchAndRender() {
      this._showLoading();
      try {
        const r = await fetch(API_URL, { headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!data || !Array.isArray(data.mainCategories)) throw new Error('Invalid response');
        this._config = data;
        this._fetchTs = Date.now();
        this._buildUI();
      } catch (err) {
        this._showError();
      }
    }

    _showLoading() {
      const tabs = this.querySelector('.armcm-tabs-inner');
      const sub  = this.querySelector('.armcm-sub-panel');
      const cont = this.querySelector('.armcm-content-panel');
      if (tabs) tabs.innerHTML = '<span class="armcm-skeleton armcm-skeleton--tab"></span><span class="armcm-skeleton armcm-skeleton--tab"></span><span class="armcm-skeleton armcm-skeleton--tab"></span>';
      if (sub)  sub.innerHTML  = Array.from({ length: 5 }, () => '<span class="armcm-skeleton armcm-skeleton--sub"></span>').join('');
      if (cont) cont.innerHTML = `<div class="armcm-circles-grid" style="grid-template-columns:repeat(3,1fr)">${Array.from({ length: 6 }, () => '<span class="armcm-skeleton armcm-skeleton--circle"></span>').join('')}</div>`;
    }

    _showError() {
      const cont = this.querySelector('.armcm-content-panel');
      if (!cont) return;
      cont.innerHTML = '<div class="armcm-error">Failed to load categories.<br><button class="armcm-error-retry">Retry</button></div>';
      cont.querySelector('.armcm-error-retry')?.addEventListener('click', () => this.refresh());
    }

    _buildUI() {
      const mains = this._enabledMains();
      if (!mains.find(c => c.id === this._selectedMainId)) {
        this._selectedMainId = mains[0]?.id ?? null;
        this._selectedSubId = null;
      }
      this._renderTabs(mains);
      this._renderSubs();
    }

    _enabledMains() {
      return (this._config?.mainCategories || [])
        .filter(c => c.enabled)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    _currentMain() {
      return this._enabledMains().find(c => c.id === this._selectedMainId) || null;
    }

    _enabledSubs() {
      return (this._currentMain()?.subcategories || [])
        .filter(s => s.enabled)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    _renderTabs(mains) {
      const tabsEl = this.querySelector('.armcm-tabs-inner');
      if (!tabsEl) return;
      tabsEl.innerHTML = mains.map(cat => {
        const active = cat.id === this._selectedMainId;
        return `<button class="armcm-tab${active ? ' armcm-tab--active' : ''}" role="tab" aria-selected="${active}" data-armcm-tab="${esc(cat.id)}">${esc(t(cat.name))}</button>`;
      }).join('');
    }

    _selectMain(id) {
      this._selectedMainId = id;
      this._selectedSubId = null;
      this._renderTabs(this._enabledMains());
      this._renderSubs();
    }

    _renderSubs() {
      const subEl = this.querySelector('.armcm-sub-panel');
      if (!subEl) return;
      const subs = this._enabledSubs();
      if (!this._selectedSubId && subs.length) this._selectedSubId = subs[0].id;

      subEl.innerHTML = subs.map(sub => {
        const active = sub.id === this._selectedSubId;
        return `<button class="armcm-sub-item${active ? ' armcm-sub-item--active' : ''}" role="option" aria-selected="${active}" data-armcm-sub="${esc(sub.id)}">${esc(t(sub.name))}</button>`;
      }).join('');

      this._renderContent();
    }

    _selectSub(id) {
      this._selectedSubId = id;
      const subEl = this.querySelector('.armcm-sub-panel');
      subEl?.querySelectorAll('.armcm-sub-item').forEach(btn => {
        const active = btn.dataset.armcmSub === id;
        btn.classList.toggle('armcm-sub-item--active', active);
        btn.setAttribute('aria-selected', String(active));
      });
      this._renderContent();
    }

    _renderContent() {
      const contEl = this.querySelector('.armcm-content-panel');
      if (!contEl) return;
      const sub = this._enabledSubs().find(s => s.id === this._selectedSubId);
      if (!sub) { contEl.innerHTML = ''; return; }

      const enabledSections = (sub.sections || []).filter(s => s.enabled);

      if (enabledSections.length) {
        const { html, sections } = renderSections(enabledSections, 0);
        contEl.innerHTML = html;
        let pgIdx = 0;
        sections.forEach(sec => {
          if (sec.type === 'product_grid') {
            fillProductGrid(contEl, sec.data, pgIdx++);
          }
        });
      } else {
        const cols = this._config?.collectionColumns || 3;
        const enabledCols = (sub.collections || [])
          .filter(c => c.enabled)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        contEl.innerHTML = `<div class="armcm-circles-grid" style="grid-template-columns:repeat(${cols},1fr)">${enabledCols.map(circleItemHtml).join('')}</div>`;
      }
    }
  }

  customElements.define('armastus-category-menu', ArmastusCategoryMenu);

  window.ArmastusCategoryMenu = {
    get el() { return document.querySelector('armastus-category-menu'); },
    open()    { this.el?.open(); },
    close()   { this.el?.close(); },
    refresh() { return this.el?.refresh(); },
    get config() { return this.el?._config; },
  };
})();
